import { useQuery } from '@tanstack/react-query'
import { getMkgStatus, getMkgVersion, getIngestStatus } from '../lib/api'
import clsx from 'clsx'
import {
  Brain, Database, Link2, Shuffle,
  CheckCircle2, XCircle, Activity, FlaskConical, Cpu, Clock,
} from 'lucide-react'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function StatusPill({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={clsx(
      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold',
      ok ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500',
    )}>
      {ok
        ? <CheckCircle2 size={11} />
        : <XCircle size={11} />}
      {label}
    </span>
  )
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
      {children}
    </h2>
  )
}

// ─── Library row ──────────────────────────────────────────────────────────────

interface LibRowProps {
  icon: React.ElementType
  iconColor: string
  iconBg: string
  name: string
  version: string
  role: string
  available: boolean
  fallback: string
}

function LibRow({ icon: Icon, iconColor, iconBg, name, version, role, available, fallback }: LibRowProps) {
  return (
    <div className="flex items-start gap-4 py-4 border-b border-gray-100 last:border-0">
      <div className={clsx('p-2 rounded-lg shrink-0', iconBg)}>
        <Icon size={18} className={iconColor} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className="font-semibold text-gray-900 text-sm">{name}</span>
          <code className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{version}</code>
          <StatusPill ok={available} label={available ? 'installed' : 'stub fallback'} />
        </div>
        <p className="text-sm text-gray-500 leading-relaxed">{role}</p>
        {!available && (
          <p className="text-xs text-amber-600 mt-1 italic">↳ {fallback}</p>
        )}
      </div>
    </div>
  )
}

// ─── Source bar ───────────────────────────────────────────────────────────────

function SourceBar({
  source, count, total, lastSynced,
}: {
  source: string
  count: number
  total: number
  lastSynced: string
}) {
  const pct = total > 0 ? (count / total) * 100 : 0
  const label = source === 'lab_upload' ? 'Lab upload' : source === 'wearable' ? 'Wearable' : 'Manual'
  const Icon = source === 'wearable' ? Activity : source === 'lab_upload' ? FlaskConical : Cpu
  const color = source === 'wearable' ? 'bg-blue-500' : source === 'lab_upload' ? 'bg-amber-500' : 'bg-violet-500'
  const iconColor = source === 'wearable' ? 'text-blue-600' : source === 'lab_upload' ? 'text-amber-600' : 'text-violet-600'
  const iconBg = source === 'wearable' ? 'bg-blue-50' : source === 'lab_upload' ? 'bg-amber-50' : 'bg-violet-50'

  const synced = lastSynced ? new Date(lastSynced).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '—'

  return (
    <div className="py-3 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-3 mb-1.5">
        <div className={clsx('p-1.5 rounded-md shrink-0', iconBg)}>
          <Icon size={13} className={iconColor} />
        </div>
        <span className="text-sm font-medium text-gray-800">{label}</span>
        <span className="ml-auto text-sm font-bold text-gray-900">{count.toLocaleString()}</span>
      </div>
      <div className="ml-8">
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-1">
          <div
            className={clsx('h-full rounded-full transition-all duration-700', color)}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-400">
          <Clock size={10} />
          Last synced: {synced}
        </div>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Engine() {
  const { data: status, isLoading: loadingStatus } = useQuery({
    queryKey: ['mkg-status'],
    queryFn: () => getMkgStatus().then(r => r.data),
  })

  const { data: version, isLoading: loadingVersion } = useQuery({
    queryKey: ['mkg-version'],
    queryFn: () => getMkgVersion().then(r => r.data),
  })

  const { data: ingest, isLoading: loadingIngest } = useQuery({
    queryKey: ['ingest-status'],
    queryFn: () => getIngestStatus().then(r => r.data),
  })

  const loading = loadingStatus || loadingVersion || loadingIngest

  const LIBS: LibRowProps[] = [
    {
      icon: Brain,
      iconColor: 'text-violet-600',
      iconBg: 'bg-violet-50',
      name: 'OpenCog Hyperon',
      version: 'hyperon==0.2.10',
      role: 'MeTTa runtime — executes causal inference rules written as s-expressions. Powers PLN rule evaluation and monitor rule declarations.',
      available: status?.hyperon_available ?? false,
      fallback: 'Native Python PLN and monitor rules produce the same CausalChain and Alert data contracts.',
    },
    {
      icon: Database,
      iconColor: 'text-blue-600',
      iconBg: 'bg-blue-50',
      name: 'Hyperon DAS',
      version: 'hyperon-das==0.9.1',
      role: 'Distributed Atomspace — scalable knowledge-graph store for personal health atoms and MKG nodes. Supports in-memory (dev) and remote server (prod) modes.',
      available: status?.hyperon_das_available ?? false,
      fallback: `_StubDAS — fully in-process dict-based store. All atom reads/writes work identically; no network required.`,
    },
    {
      icon: Link2,
      iconColor: 'text-amber-600',
      iconBg: 'bg-amber-50',
      name: 'cosmpy / ASI:Chain',
      version: 'cosmpy==0.9.2',
      role: 'Cosmos SDK for ASI:Chain — broadcasts ConsentContracts on-chain, handles ASI token economy (PLN: 0.40 ASI, MOSES: 1.00 ASI), anchors DIDs. Phase 2.',
      available: status?.cosmpy_available ?? false,
      fallback: 'Consent contracts stored in SQLite; token balances are stubs. On-chain broadcast prepared but gated on a funded wallet.',
    },
    {
      icon: Shuffle,
      iconColor: 'text-emerald-600',
      iconBg: 'bg-emerald-50',
      name: 'MOSES / asmoses',
      version: 'asmoses (optional)',
      role: 'Meta-Optimizing Semantic Evolutionary Search — discovers multigenerational if-then health patterns across a family\'s combined Atomspace.',
      available: false,
      fallback: '_native_moses() combinatorial fallback finds conditions shared across ≥2 generations and returns identical MOSESPatternAtom output.',
    },
  ]

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">

      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h2 className="text-2xl font-bold text-gray-900">DAS Engine</h2>
        <p className="text-sm text-gray-500 mt-0.5">SingularityNET library status and atom store details</p>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : (
        <div className="space-y-6">

          {/* Active engine badge */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 flex flex-wrap sm:flex-nowrap items-center gap-4">
            <div className="p-3 rounded-xl bg-gray-100">
              <Database size={22} className="text-gray-500" />
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Active engine</p>
              <p className="text-2xl font-bold text-gray-900 font-mono">{status?.das_type ?? '—'}</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">MKG version</p>
              <p className="text-lg font-bold text-gray-900">{version?.current_version ?? '—'}</p>
              <p className="text-xs text-gray-400">
                updated {version?.last_updated ?? '—'} · {version?.atom_count ?? 0} MKG nodes
              </p>
            </div>
          </div>

          {/* Library availability */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <SectionHeading>Library availability</SectionHeading>
            <div className="divide-y divide-gray-100">
              {LIBS.map(lib => <LibRow key={lib.name} {...lib} />)}
            </div>
          </div>

          {/* Atom sources */}
          {ingest && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <SectionHeading>Atom sources</SectionHeading>
              <div className="mb-3 flex items-center justify-between">
                <span className="text-xs text-gray-400">
                  {ingest.sources?.length ?? 0} active source{ingest.sources?.length !== 1 ? 's' : ''}
                </span>
                <span className="text-sm font-bold text-gray-900">
                  {(ingest.total_atoms ?? 0).toLocaleString()} total atoms
                </span>
              </div>
              {(ingest.sources ?? []).map((s: { source: string; atom_count: number; last_synced: string }) => (
                <SourceBar
                  key={s.source}
                  source={s.source}
                  count={s.atom_count}
                  total={ingest.total_atoms}
                  lastSynced={s.last_synced}
                />
              ))}
            </div>
          )}

          {/* Explanation */}
          <div className="bg-brand-50 border border-brand-100 rounded-xl p-5">
            <p className="text-xs font-semibold text-brand-700 uppercase tracking-wide mb-2">
              Why stub fallbacks?
            </p>
            <p className="text-sm text-brand-800 leading-relaxed">
              <code className="bg-brand-100 px-1 rounded text-xs">hyperon-das-atomdb</code> is distributed
              via SingularityNET's internal registry — not public PyPI. FamilyPortal's{' '}
              <code className="bg-brand-100 px-1 rounded text-xs">_StubDAS</code> and native PLN/MOSES
              engines produce identical data contracts, so every feature works in development without
              any SingularityNET infrastructure. Connecting to a live DAS node requires swapping one
              environment variable (<code className="bg-brand-100 px-1 rounded text-xs">DAS_IN_MEMORY=false</code>)
              and providing a <code className="bg-brand-100 px-1 rounded text-xs">DAS_HOST</code>.
            </p>
          </div>

        </div>
      )}
    </div>
  )
}
